/**
 * Root route error UI: shown when a route error bubbles to the root (no nearer errorComponent).
 * Wrapped in I18nProvider so translations work even if RootLayout did not mount fully.
 */
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import React from 'react';

import { I18nProvider, useTranslation } from '~/app';
import { env } from '~/app/env';
import { Button } from '~/components/Form/Button';

function RootRouteErrorInner({ error, reset }: ErrorComponentProps): JSX.Element {
  const { t } = useTranslation();
  const router = useRouter();
  const message = error instanceof Error ? error.message : String(error);

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 py-8 text-center">
      <h1 className="text-xl font-semibold text-gray-900">{t('error_route_title')}</h1>
      <p className="max-w-md text-sm text-gray-600">{t('error_route_description')}</p>
      {env.dev && (
        <pre className="max-h-40 max-w-full overflow-auto rounded bg-gray-100 p-3 text-left text-xs whitespace-pre-wrap text-red-800">
          {message}
        </pre>
      )}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button type="button" variant="primary" onClick={() => reset()}>
          {t('error_route_tryAgain')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            void router.navigate({ to: '/' });
          }}
        >
          {t('auth_action_go_to_login')}
        </Button>
      </div>
    </div>
  );
}

export function RootRouteError(props: ErrorComponentProps): JSX.Element {
  return (
    <I18nProvider urlPath="/">
      <RootRouteErrorInner {...props} />
    </I18nProvider>
  );
}
